import { type PermissionModule, crud } from './permission-defs';

/** The back of the lab: stock, money, analytics, imports and administration. */
export const OPERATIONS_MODULES = [
  {
    key: 'inventory',
    label: 'Inventory',
    description: 'Reagents, stock and suppliers.',
    items: [
      { label: 'Suppliers', permissions: crud('supplier', 'supplier') },
      { label: 'Reagent items', permissions: crud('reagentItem', 'reagent item') },
      { label: 'Consumption recipes', permissions: crud('recipe', 'consumption recipe') },
      {
        label: 'Lots',
        permissions: {
          'lot.view': { action: 'View', description: 'See reagent lots.' },
          'lot.create': {
            action: 'Receive',
            description: 'Enter a new lot onto the shelf.',
            requires: ['lot.view'],
          },
          'lot.setStatus': {
            action: 'Quarantine / release',
            description: 'Change a lot’s status.',
            requires: ['lot.view'],
          },
        },
      },
      {
        label: 'Stock',
        permissions: {
          'stock.view': {
            action: 'View',
            description: 'The stock ledger, low-stock and expiring lists, and test availability.',
          },
          'stock.adjust': {
            action: 'Adjust',
            description: 'Correct a stock count.',
            requires: ['stock.view'],
          },
          'stock.wastage': {
            action: 'Write off',
            description: 'Record wastage.',
            requires: ['stock.view'],
          },
          'stock.reconcile': {
            action: 'Reconcile',
            description: 'Find and repair drift between the ledger and lot balances.',
            requires: ['stock.view'],
          },
          'inventory.coverage': {
            action: 'Coverage',
            description: 'Tests that consume nothing the shelf tracks.',
          },
        },
      },
      {
        label: 'Requisitions',
        permissions: {
          'requisition.view': { action: 'View', description: 'See purchase requisitions.' },
          'requisition.create': {
            action: 'Create',
            description: 'Raise a requisition.',
            requires: ['requisition.view'],
          },
          'requisition.edit': {
            action: 'Edit',
            description: 'Change a draft requisition.',
            requires: ['requisition.view'],
          },
          'requisition.delete': {
            action: 'Delete',
            description: 'Delete a draft requisition.',
            requires: ['requisition.view'],
          },
          'requisition.submit': {
            action: 'Submit',
            description: 'Send a draft for approval.',
            requires: ['requisition.view'],
          },
          'requisition.cancel': {
            action: 'Cancel',
            description: 'Withdraw a requisition.',
            requires: ['requisition.view'],
          },
          'requisition.approve': {
            action: 'Approve',
            description: 'Approve a submitted requisition.',
            requires: ['requisition.view'],
            sensitive: true,
          },
          'requisition.reject': {
            action: 'Reject',
            description: 'Reject a submitted requisition.',
            requires: ['requisition.view'],
          },
          'requisition.order': {
            action: 'Order',
            description: 'Mark an approved requisition as ordered.',
            requires: ['requisition.view'],
            sensitive: true,
          },
          'requisition.receive': {
            action: 'Receive',
            description: 'Record a delivery against a requisition.',
            requires: ['requisition.view'],
          },
        },
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    description: 'Money: billing figures, the day close and the ledgers.',
    items: [
      {
        label: 'Billing figures',
        permissions: {
          'billing.view': {
            action: 'See money',
            description:
              'Amounts billed, paid and owed on screens everyone shares. Without it they are withheld.',
            sensitive: true,
          },
        },
      },
      {
        label: 'Day close',
        permissions: {
          'dayClose.view': { action: 'View own', description: 'One’s own shift summary.' },
          'dayClose.submit': {
            action: 'Submit',
            description: 'Submit one’s own day close at sign-out.',
            requires: ['dayClose.view'],
          },
          'dayClose.viewAll': {
            action: 'View everyone',
            description: 'Every staff member’s day close, not only one’s own.',
            requires: ['dayClose.view'],
            sensitive: true,
          },
          'dayClose.review': {
            action: 'Review',
            description: 'Reconcile and sign off submissions.',
            requires: ['dayClose.viewAll'],
            sensitive: true,
          },
        },
      },
      {
        label: 'Reports',
        permissions: {
          'finance.income': {
            action: 'Income',
            description: 'The income report.',
            sensitive: true,
          },
          'finance.profitLoss': {
            action: 'Profit & loss',
            description: 'The profit and loss statement.',
            sensitive: true,
          },
          'finance.yearHistory': {
            action: 'Year history',
            description: 'Year-by-year volume (money only with “See money”).',
          },
        },
      },
      {
        label: 'Expenses',
        permissions: {
          ...crud('expense', 'expense'),
          'expenseCategory.manage': {
            action: 'Categories',
            description: 'Add expense categories.',
            requires: ['expense.view'],
          },
        },
      },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Operational reports about the lab’s work.',
    items: [
      {
        label: 'Analytics',
        permissions: {
          'analytics.view': {
            action: 'View',
            description: 'Overview, volume, patients, flow and quality.',
          },
          'analytics.staff': {
            action: 'Staff (own)',
            description: 'One’s own productivity row and the lab-wide total.',
          },
          'analytics.staffAll': {
            action: 'Staff (everyone)',
            description: 'Every staff member’s productivity by name.',
            requires: ['analytics.staff'],
            sensitive: true,
          },
          'analytics.supply': {
            action: 'Supply',
            description: 'Reagent consumption and supply analytics.',
          },
        },
      },
    ],
  },
  {
    key: 'imports',
    label: 'Imports',
    description: 'Bulk import from spreadsheets.',
    items: [
      {
        label: 'Imports',
        permissions: {
          'import.view': {
            action: 'View',
            description: 'See past imports and download templates.',
          },
          'import.run': {
            action: 'Run',
            description: 'Upload, map, validate and commit an import — it rewrites live data.',
            requires: ['import.view'],
            sensitive: true,
          },
          'import.delete': {
            action: 'Delete',
            description: 'Delete an import run.',
            requires: ['import.view'],
          },
          'importAlias.manage': {
            action: 'Aliases',
            description: 'Manage test-name aliases used to match imported rows.',
            requires: ['import.view'],
          },
        },
      },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    description: 'Staff, roles and lab-wide settings.',
    items: [
      {
        label: 'Staff',
        permissions: {
          ...crud('staff', 'staff member'),
          'staff.permissions': {
            action: 'Permissions',
            description: 'Grant or deny individual permissions to one staff member.',
            requires: ['staff.view', 'role.view'],
            sensitive: true,
          },
        },
      },
      {
        label: 'Roles',
        permissions: {
          'role.view': { action: 'View', description: 'See roles and what they allow.' },
          'role.manage': {
            action: 'Manage',
            description: 'Create, change and delete roles.',
            requires: ['role.view'],
            sensitive: true,
          },
        },
      },
      {
        label: 'Settings',
        permissions: {
          'labProfile.edit': {
            action: 'Lab profile',
            description: 'The lab’s name, address, licence numbers and logo.',
          },
          'backup.export': {
            action: 'Backup',
            description: 'Download every patient, result and payment as a workbook.',
            sensitive: true,
          },
          'system.health': {
            action: 'Database health',
            description: 'Connections, cache and slow statements.',
            sensitive: true,
          },
        },
      },
    ],
  },
] as const satisfies readonly PermissionModule[];
